import { IconArrowRight, IconCheckbox } from '@tabler/icons-react-native';
import { presentInsuredDetailsAtom } from 'app/atoms';
import { PartagePlusBadge } from 'app/components/partage-plus-badge';
import { InsuredUserDetails } from 'app/components/insured-users';
import { LogoRenderer } from 'app/design/branding/logo';
import { LogoOneLine } from 'app/design/icons/logo-one-line';
import { BottomSheet } from 'app/design/primitives/bottom-sheet';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { addMonths, localeAwareFormat } from 'app/lib/date';
import { env } from 'app/lib/env';
import { isFreeTrialPlanCode, useMyActivePlans } from 'app/network/plan';
import { useProfile } from 'app/network/profile';
import { useUser, useUserCurrency } from 'app/network/user';
import { PaymentContent } from 'app/screens/profile/payment';
import { PlanRecurrenceEnum } from 'app/schemas/plan';
import { useTheme } from 'app/shopify';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isNotWhiteLabel } from 'app/utils/helpers';
import { useSetAtom } from 'jotai';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, Pressable, ScrollView, useWindowDimensions, View } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, { Easing, FadeInRight, FadeOutLeft, useReducedMotion } from 'react-native-reanimated';
import type { usePresentCommunityScreen } from './use-present-community-screen';
import { InsuredBadge } from 'app/design/icons/insured-badge';
import type { Plan } from 'app/types/plan';

type PresentCommunityScreenState = ReturnType<typeof usePresentCommunityScreen>;
type Step = 'welcome' | 'subscription' | 'success';

function PrimaryButton({
  label,
  loading = false,
  onPress,
  showArrow = true,
}: {
  label: string;
  loading?: boolean;
  onPress: () => void;
  showArrow?: boolean;
}) {
  const theme = useTheme();
  return (
    <Button
      disabled={loading}
      loading={loading}
      onPress={onPress}
      variant="primary"
      width="100%"
      style={{ borderRadius: theme.borderRadii['radius.full'], minHeight: 56 }}
    >
      <Row alignItems="center" justifyContent="center" gap="spacing.general.x2">
        <Text color="text.inverse.primary" variant="bodyBaseMedium">
          {label}
        </Text>
        {showArrow ? (
          <IconArrowRight color={theme.colors['text.inverse.primary']} size={16} strokeWidth={ICON_STROKE_WIDTH} />
        ) : null}
      </Row>
    </Button>
  );
}

function CheckLine({ children, description }: { children: ReactNode; description?: string }) {
  const theme = useTheme();

  return (
    <Row alignItems={!description ? 'center' : 'flex-start'} gap="spacing.general.x4">
      <IconCheckbox color={theme.colors['icon.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />
      <Column flex={1} gap="spacing.general.x1">
        <Text color="text.primary" variant="bodyBaseStrong">
          {children}
        </Text>
        {description ? (
          <Text color="text.secondary" variant="bodyBase">
            {description}
          </Text>
        ) : null}
      </Column>
    </Row>
  );
}

function SuccessFeature({ children, highlighted = false }: { children: ReactNode; highlighted?: boolean }) {
  const theme = useTheme();
  return (
    <Row
      alignItems="center"
      backgroundColor={highlighted ? 'surface.secondary-alt' : undefined}
      borderRadius={highlighted ? 'radius.md' : undefined}
      gap={highlighted ? 'spacing.general.x2' : 'spacing.general.x4'}
      padding={highlighted ? 'spacing.general.x2' : undefined}
      paddingHorizontal={highlighted ? undefined : 'spacing.general.x2'}
      width="100%"
    >
      {highlighted ? (
        <InsuredBadge color={theme.colors['surface.default']} height={30} width={30} />
      ) : (
        <IconCheckbox color={theme.colors['icon.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />
      )}
      <Column flex={1}>
        <Text color="text.primary" variant="bodyBaseMedium">
          {children}
        </Text>
      </Column>
    </Row>
  );
}

function ScreenShell({
  background = 'standard',
  children,
  footer,
  success = false,
}: {
  background?: 'premium' | 'standard';
  children: ReactNode;
  footer: ReactNode;
  success?: boolean;
}) {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const [footerHeight, setFooterHeight] = useState(180);
  const useCoreOneLineLogo = isNotWhiteLabel() && !env.IS_PARTAKE;
  const backgroundColor =
    background === 'premium'
      ? theme.colors['surface.brand.secondary-5.subtle']
      : theme.colors[success ? 'surface.secondary-alt' : 'surface.secondary'];
  const logoWidth = 121;
  return (
    <SafeAreaView edges={['top', 'left', 'right']} style={{ flex: 1, backgroundColor }}>
      <ScrollView
        contentContainerStyle={{
          alignItems: 'center',
          gap: theme.spacing['spacing.general.x8'],
          paddingHorizontal: theme.spacing['spacing.general.x4'],
          paddingTop: theme.spacing['spacing.general.x4'],
          paddingBottom: footerHeight + theme.spacing['spacing.general.x4'],
        }}
        showsVerticalScrollIndicator={false}
      >
        {useCoreOneLineLogo ? (
          <LogoOneLine color={theme.colors['text.primary']} height={(logoWidth * 576) / 1584} width={logoWidth} />
        ) : (
          <LogoRenderer color={theme.colors['text.primary']} width={101} />
        )}
        {children}
      </ScrollView>
      <View
        onLayout={(event) => setFooterHeight(event.nativeEvent.layout.height)}
        style={{
          bottom: 0,
          left: 0,
          paddingBottom: insets.bottom + theme.spacing['spacing.general.x4'],
          paddingHorizontal: theme.spacing['spacing.general.x4'],
          position: 'absolute',
          right: 0,
        }}
      >
        {footer}
      </View>
    </SafeAreaView>
  );
}

function Hero({
  title,
  subtitle,
  celebration = true,
  celebrationFontSize = 68.571,
  subtitleWidth,
}: {
  title: string;
  subtitle: string;
  celebration?: boolean;
  celebrationFontSize?: number;
  subtitleWidth?: number;
}) {
  return (
    <Column alignItems="center" gap="spacing.general.x4" width="100%">
      {celebration ? (
        <Text fontSize={celebrationFontSize} lineHeight={celebrationFontSize * 1.2}>
          🎉
        </Text>
      ) : null}
      <Text color="text.primary" fontWeight="600" lineHeight={48} textAlign="center" variant="headerLarge">
        {title}
      </Text>
      <Text
        color="text.primary"
        lineHeight={27}
        style={subtitleWidth ? { maxWidth: subtitleWidth } : undefined}
        textAlign="center"
        fontWeight="400"
        variant="bodyLarge"
      >
        {subtitle}
      </Text>
    </Column>
  );
}

function WelcomeStep({ subscribed, plan, onContinue }: { subscribed: boolean; plan?: Plan; onContinue: () => void }) {
  const { t } = useScopedTranslation('presentCommunity');
  const presentInsuredDetails = useSetAtom(presentInsuredDetailsAtom);
  const provider = plan?.code.group?.name ?? 'Partage Club';
  const providerPicture = plan?.code.group?.picture;
  const theme = useTheme();
  return (
    <ScreenShell
      footer={
        <PrimaryButton
          label={t(subscribed ? 'newFlow.startSharing' : 'newFlow.continue')}
          onPress={onContinue}
          showArrow={!subscribed}
        />
      }
    >
      {subscribed ? (
        <>
          <Column alignItems="center" gap="spacing.general.x4" width="100%">
            <View style={{ height: 120, width: 120 }}>
              <View
                style={{
                  alignItems: 'center',
                  backgroundColor: theme.colors['surface.default'],
                  borderColor: theme.colors['border.secondary'],
                  borderRadius: theme.borderRadii['radius.full'],
                  borderWidth: 1,
                  height: 120,
                  justifyContent: 'center',
                  overflow: 'hidden',
                  width: 120,
                }}
              >
                {providerPicture ? (
                  <Image
                    accessibilityLabel={provider}
                    resizeMode="contain"
                    source={{ uri: providerPicture }}
                    style={{ backgroundColor: '#fff', height: '100%', width: '100%' }}
                  />
                ) : (
                  <Text color="text.primary" variant="headerLarge">
                    {provider.slice(0, 1)}
                  </Text>
                )}
              </View>
              <View
                style={{
                  alignItems: 'center',
                  backgroundColor: theme.colors['surface.brand.secondary-4.subtle'],
                  borderRadius: theme.borderRadii['radius.full'],
                  bottom: 0,
                  height: 40,
                  justifyContent: 'center',
                  position: 'absolute',
                  right: 0,
                  width: 40,
                }}
              >
                <Text fontSize={24}>🎉</Text>
              </View>
            </View>
            <Column alignItems="center" gap="spacing.general.x4" width="100%">
              <Text color="text.primary" fontWeight="600" lineHeight={48} textAlign="center" variant="headerLarge">
                {t('newFlow.sponsored.title')}
              </Text>
              <Column alignItems="center" width="100%">
                <Text color="text.primary" lineHeight={27} textAlign="center" variant="bodyLarge">
                  {t('newFlow.sponsored.subtitle')}
                </Text>
                <Text color="text.primary" lineHeight={27} textAlign="center" variant="bodyLargeStrong">
                  {provider}
                </Text>
              </Column>
            </Column>
          </Column>
          <Column backgroundColor="surface.default" borderRadius="radius.lg" padding="spacing.general.x3" width="100%">
            <Column
              backgroundColor="surface.secondary-neutral"
              borderRadius="radius.lg"
              gap="spacing.general.x2"
              padding="spacing.general.x3"
            >
              <Row alignItems="center" gap="spacing.general.x1">
                <Text color="text.primary" variant="bodyBaseStrong">
                  {t('newFlow.includedWith')}
                </Text>
                <PartagePlusBadge />
                <Text color="text.primary" variant="bodyBaseStrong">
                  :
                </Text>
              </Row>
              <Column gap="spacing.general.x1" paddingVertical="spacing.general.x2">
                <IncludedBenefit>{t('newFlow.unlimitedLending')}</IncludedBenefit>
                <IncludedBenefit>{t('newFlow.unlimitedBorrowing')}</IncludedBenefit>
              </Column>
              <Row
                alignItems="center"
                backgroundColor="surface.secondary-alt"
                borderRadius="radius.sm"
                gap="spacing.general.x2"
                padding="spacing.general.x2"
              >
                <InsuredBadge color={theme.colors['surface.default']} height={30} width={30} />
                <Text color="text.primary" flex={1} variant="bodyBaseMedium">
                  {t('newFlow.damageCoverage')}*
                </Text>
              </Row>
            </Column>
            <Text color="text.secondary" marginTop="spacing.general.x2" variant="bodySmall">
              {t('newFlow.coverageFootnote')}{' '}
              <Text
                accessibilityRole="link"
                color="text.secondary"
                onPress={() => presentInsuredDetails({ insured: true })}
                textDecorationLine="underline"
                variant="bodySmallMedium"
              >
                {t('newFlow.learnMore').replace(/ /g, '\u00a0')}
              </Text>
            </Text>
          </Column>
        </>
      ) : (
        <>
          <Hero title={t('newFlow.free.title')} subtitle={t('newFlow.free.subtitle')} />
          <Column
            backgroundColor="surface.default"
            borderRadius="radius.lg"
            gap="spacing.general.x6"
            padding="spacing.general.x6"
            width="100%"
          >
            <Column gap="spacing.general.x6">
              <Text color="text.primary" variant="bodyBaseMedium">
                {t('newFlow.includedToday')}
              </Text>
              <CheckLine description={t('newFlow.unlimitedLendingDescription')}>
                {t('newFlow.unlimitedLending')}
              </CheckLine>
              <CheckLine description={t('newFlow.oneFreeBorrowDescription')}>
                <Text color="text.primary" variant="bodyBaseStrong">
                  {t('newFlow.oneFreeBorrowPrefix')}{' '}
                  <Text color="text.emphasized" variant="bodyBaseStrong">
                    {t('newFlow.oneFreeBorrowHighlight')}
                  </Text>
                </Text>
              </CheckLine>
            </Column>
          </Column>
        </>
      )}
    </ScreenShell>
  );
}

function IncludedBenefit({ children }: { children: ReactNode }) {
  const theme = useTheme();

  return (
    <Row
      alignItems="center"
      gap="spacing.general.x2"
      paddingHorizontal="spacing.general.x2"
      paddingVertical="spacing.general.x1"
    >
      <IconCheckbox color={theme.colors['icon.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />
      <Text color="text.primary" flex={1} variant="bodyBaseMedium">
        {children}
      </Text>
    </Row>
  );
}

function SubscriptionStep({ onFree, onSubscribe }: { onFree: () => void; onSubscribe: () => void }) {
  const { t } = useScopedTranslation('presentCommunity');
  const setInsuredDetailsOtherUser = useSetAtom(presentInsuredDetailsAtom);
  const currency = useUserCurrency();

  const theme = useTheme();
  return (
    <ScreenShell
      background="premium"
      footer={
        <Column gap="spacing.general.x3" width="100%">
          <Text color="text.secondary" textAlign="center" variant="bodySmall">
            {t('newFlow.yearlyPrice', {
              annualPrice: t(`price.${PlanRecurrenceEnum.yearly}.${currency}`, { ns: 'payment' }),
              monthlyPrice: t(`monthlyEquivalent.${currency}`, { ns: 'payment' }),
            })}
          </Text>
          <PrimaryButton label={t('newFlow.subscribeNow')} onPress={onSubscribe} />
          <Button
            onPress={onFree}
            variant="plain"
            width="100%"
            style={{
              alignItems: 'center',
              borderColor: theme.colors['text.primary'],
              borderRadius: theme.borderRadii['radius.full'],
              borderWidth: 1,
              justifyContent: 'center',
              minHeight: 56,
            }}
          >
            <Text color="text.primary" variant="bodyBaseMedium">
              {t('newFlow.continueFree')}
            </Text>
          </Button>
        </Column>
      }
    >
      <Hero celebration={false} title={t('newFlow.upsell.title')} subtitle={t('newFlow.upsell.subtitle')} />
      <Column
        backgroundColor="surface.default"
        borderRadius="radius.lg"
        gap="spacing.general.x4"
        padding="spacing.general.x4"
        width="100%"
      >
        <Row alignItems="center" gap="spacing.general.x1">
          <Text color="text.primary" variant="bodyBaseStrong">
            {t('newFlow.upsell.withSubscription')}
          </Text>
          <PartagePlusBadge />
          <Text color="text.primary" variant="bodyBaseStrong">
            :
          </Text>
        </Row>
        <Column marginLeft="spacing.general.x4" gap="spacing.general.x4">
          <CheckLine>{t('newFlow.unlimitedLending')}</CheckLine>
          <CheckLine>{t('newFlow.unlimitedBorrowing')}</CheckLine>
        </Column>
        <Column
          backgroundColor="surface.secondary"
          borderRadius="radius.lg"
          gap="spacing.general.x1"
          padding="spacing.general.x4"
        >
          <Row alignItems="flex-start" gap="spacing.general.x2">
            <InsuredBadge color={theme.colors['surface.default']} height={30} width={30} />
            <Column flex={1} gap="spacing.general.x1">
              <Text color="text.primary" variant="bodyBaseStrong">
                {t('newFlow.damageCoverage')}
              </Text>
              <Text color="text.secondary" variant="bodyBase">
                {t('newFlow.upsell.damageCoverageDescription')}
              </Text>
              <Pressable onPress={() => setInsuredDetailsOtherUser({ insured: true })}>
                <Text color="text.primary" textDecorationLine="underline" variant="bodySmall">
                  {t('newFlow.learnMore')}
                </Text>
              </Pressable>
            </Column>
          </Row>
        </Column>
      </Column>
    </ScreenShell>
  );
}

export function SubscriptionSuccess({
  isContinuing = false,
  onContinue,
}: {
  isContinuing?: boolean;
  onContinue: () => void;
}) {
  const { t } = useTranslation('presentCommunity');
  const theme = useTheme();
  const user = useUser();
  const profile = useProfile(user.data?.id);
  const plan = useMyActivePlans().data?.[0];
  const paidPlan = plan && !isFreeTrialPlanCode(plan.code.code) ? plan : undefined;
  const currency = useUserCurrency();
  const recurrence = paidPlan?.recurrence ?? PlanRecurrenceEnum.yearly;
  const isRecurring = recurrence === PlanRecurrenceEnum.monthly || recurrence === PlanRecurrenceEnum.yearly;
  const fallbackExpiry = addMonths(new Date(), recurrence === PlanRecurrenceEnum.monthly ? 1 : 12);
  const expiry = isRecurring
    ? localeAwareFormat(
        paidPlan?.expiresAt ? new Date(paidPlan.expiresAt) : fallbackExpiry,
        t('dateFormat_MMMMdYYYY', { ns: 'common' }),
      )
    : undefined;
  const price = t(`price.${recurrence}.${currency}`, { ns: 'payment' });
  const currencySuffix = currency === 'CAD' ? ' CAD' : '';
  const formattedPrice = `${price}${currencySuffix}`;
  return (
    <ScreenShell
      footer={
        <PrimaryButton
          label={t('newFlow.startSharing')}
          loading={isContinuing}
          onPress={onContinue}
          showArrow={false}
        />
      }
      success
    >
      <Hero
        celebrationFontSize={68.571}
        subtitleWidth={295}
        title={t('newFlow.success.title', { name: profile.data?.firstName ?? '' })}
        subtitle={t('newFlow.success.subtitle')}
      />
      <Column
        backgroundColor="surface.default"
        borderRadius="radius.lg"
        gap="spacing.general.x6"
        padding="spacing.general.x6"
        width="100%"
      >
        <Row alignItems="center" gap="spacing.general.x2">
          <Text color="text.primary" variant="bodyBaseStrong">
            {t('newFlow.success.yourSubscription')}
          </Text>
          <PartagePlusBadge />
        </Row>
        <Column
          borderColor="border.primary"
          borderRadius="radius.lg"
          borderWidth={1}
          gap="spacing.general.x4"
          padding="spacing.general.x6"
        >
          {isRecurring ? (
            <Text color="text.primary" variant="bodyBaseStrong">
              {formattedPrice}/{t(`period.${recurrence}`, { ns: 'payment' })}*
            </Text>
          ) : null}
          {isRecurring ? (
            <Text color="text.secondary" variant="bodyXSmall">
              {t(`newFlow.success.billingNote.${recurrence}`, { price: formattedPrice })}
            </Text>
          ) : null}
          {expiry ? <View style={{ height: 1, backgroundColor: theme.colors['border.secondary'] }} /> : null}
          {expiry ? (
            <Column gap="spacing.general.x2">
              <Text color="text.primary" variant="bodyBase">
                {t('newFlow.success.nextPayment')}
              </Text>
              <Text color="text.primary" variant="bodyBaseStrong">
                {expiry}
              </Text>
            </Column>
          ) : null}
        </Column>
        <Column gap="spacing.general.x3" width="100%">
          <SuccessFeature>{t('newFlow.unlimitedLending')}</SuccessFeature>
          <SuccessFeature>{t('newFlow.unlimitedBorrowing')}</SuccessFeature>
          <SuccessFeature highlighted>{t('newFlow.damageCoverage')}</SuccessFeature>
        </Column>
      </Column>
    </ScreenShell>
  );
}

export function PresentCommunityScreenBody({ finishedIntro, isFinishing }: PresentCommunityScreenState) {
  const { data: plans, isLoading } = useMyActivePlans({ throwOnError: true });
  const plan = plans?.[0];
  const subscribed = Boolean(plan && !isFreeTrialPlanCode(plan.code.code));
  const [step, setStep] = useState<Step>('welcome');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const { height: deviceHeight } = useWindowDimensions();
  const paymentSheetHeight = Math.min(deviceHeight * 0.92, deviceHeight - 90);
  const enteringTransition = reducedMotion ? undefined : FadeInRight.duration(320).easing(Easing.out(Easing.cubic));
  const exitingTransition = reducedMotion ? undefined : FadeOutLeft.duration(220).easing(Easing.in(Easing.cubic));

  if (isLoading) return <ActivityIndicator style={{ flex: 1 }} />;

  return (
    <View style={{ flex: 1, overflow: 'hidden' }}>
      <Animated.View
        key={step}
        entering={enteringTransition}
        exiting={exitingTransition}
        style={{ bottom: 0, left: 0, position: 'absolute', right: 0, top: 0 }}
      >
        {step === 'welcome' ? (
          <WelcomeStep
            subscribed={subscribed}
            plan={plan}
            onContinue={() => (subscribed ? void finishedIntro(true) : setStep('subscription'))}
          />
        ) : step === 'subscription' ? (
          <SubscriptionStep onFree={() => void finishedIntro(true)} onSubscribe={() => setPaymentOpen(true)} />
        ) : (
          <SubscriptionSuccess isContinuing={isFinishing} onContinue={() => void finishedIntro(true)} />
        )}
      </Animated.View>
      <BottomSheet
        containerStyle={{ backgroundColor: theme.colors['surface.default'], height: paymentSheetHeight }}
        contentStyle={{ height: paymentSheetHeight - 60 }}
        onClose={() => setPaymentOpen(false)}
        open={paymentOpen}
      >
        {paymentOpen ? (
          <View style={{ flex: 1 }}>
            <PaymentContent
              embedded
              onSuccess={() => {
                setPaymentOpen(false);
                setStep('success');
              }}
            />
          </View>
        ) : null}
      </BottomSheet>
      <InsuredUserDetails />
    </View>
  );
}
